import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import MyCourses from "./pages/MyCourses";
import Catalog from "./pages/Catalog";
import CourseDetail from "./pages/CourseDetail";
import CoursePlayer from "./pages/CoursePlayer";
import Quiz from "./pages/Quiz";
import ProgressPage from "./pages/Progress";
import Settings from "./pages/Settings";
import Certificates from "./pages/Certificates";
import CertificateViewer from "./pages/CertificateViewer";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminCourses from "./pages/admin/AdminCourses";
import AdminCertificates from "./pages/admin/AdminCertificates";
import AdminEnrollments from "./pages/admin/AdminEnrollments";
import AdminLeads from "./pages/admin/AdminLeads";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminDiscounts from "./pages/admin/AdminDiscounts";
import AdminBroadcasts from "./pages/admin/AdminBroadcasts";
import AdminRefunds from "./pages/admin/AdminRefunds";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import Wishlist from "./pages/Wishlist";
import Cart from "./pages/Cart";
import Community from "./pages/Community";

// Redirect admins away from student pages
function StudentRoute({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) return null;
    if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    return children;
}

// Redirect to login if not authenticated
function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div style={{
                minHeight: "100vh", background: "#0A0B0E",
                display: "flex", alignItems: "center", justifyContent: "center"
            }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid rgba(207,255,80,0.2)", borderTopColor: "#CFFF50", animation: "spin 0.8s linear infinite" }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return children;
}

// Redirect to dashboard if not admin
function AdminRoute({ children }) {
    const { user, loading } = useAuth();

    if (loading) return null;

    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

    return children;
}

function AppRouter() {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/catalog" element={<Catalog />} />
            <Route path="/catalog/:courseId" element={<CourseDetail />} />

            {/* Protected — must be logged in, students only */}
            <Route path="/dashboard" element={<StudentRoute><Dashboard /></StudentRoute>} />
            <Route path="/my-courses" element={<StudentRoute><MyCourses /></StudentRoute>} />
            <Route path="/learn/:courseId" element={<StudentRoute><CoursePlayer /></StudentRoute>} />
            <Route path="/learn/:courseId/quiz/:quizId" element={<StudentRoute><Quiz /></StudentRoute>} />
            <Route path="/community/:courseId" element={<StudentRoute><Community /></StudentRoute>} />
            <Route path="/wishlist" element={<StudentRoute><Wishlist /></StudentRoute>} />
            <Route path="/cart" element={<StudentRoute><Cart /></StudentRoute>} />
            <Route path="/progress" element={<StudentRoute><ProgressPage /></StudentRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/certificates" element={<StudentRoute><Certificates /></StudentRoute>} />
            <Route path="/certificates/:certId" element={<StudentRoute><CertificateViewer /></StudentRoute>} />

            {/* Admin only */}
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/analytics" element={<AdminRoute><AdminAnalytics /></AdminRoute>} />
            <Route path="/admin/customers" element={<AdminRoute><AdminCustomers /></AdminRoute>} />
            <Route path="/admin/courses" element={<AdminRoute><AdminCourses /></AdminRoute>} />
            <Route path="/admin/enrollments" element={<AdminRoute><AdminEnrollments /></AdminRoute>} />
            <Route path="/admin/certificates" element={<AdminRoute><AdminCertificates /></AdminRoute>} />
            <Route path="/admin/leads" element={<AdminRoute><AdminLeads /></AdminRoute>} />
            <Route path="/admin/discounts" element={<AdminRoute><AdminDiscounts /></AdminRoute>} />
            <Route path="/admin/broadcasts" element={<AdminRoute><AdminBroadcasts /></AdminRoute>} />
            <Route path="/admin/refunds" element={<AdminRoute><AdminRefunds /></AdminRoute>} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <Toaster
                        theme="dark"
                        position="top-right"
                        toastOptions={{
                            style: { background: "#1A1C20", border: "1px solid rgba(255,255,255,0.08)", color: "#ffffff" },
                        }}
                    />
                    <AppRouter />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
